import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { restrictToParentElement, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useEffect, useState, type JSX } from "react";
import { NavLink } from "react-router-dom";

import type { AppDescriptorDTO } from "../api/types.js";
import { moveApp, orderApps } from "../apps/appOrder.js";
import { AppIcon } from "./AppIcon.js";

/**
 * Die Bereiche von buma in drei Erscheinungsformen:
 *
 * - `rail`   — schmale Leiste am linken Rand, nur Symbole. Erst ab großen Fenstern.
 * - `icons`  — dieselbe Leiste, aber immer sichtbar: für die Schublade auf dem Telefon.
 * - `bottom` — Leiste am unteren Rand des Telefons, immer sichtbar, mit Beschriftung.
 *
 * Die untere Leiste zeigt bis zu sechs Einträge; sobald buma mehr Apps hat, als dort
 * hineinpassen, führt die Schublade zu allen.
 *
 * Die senkrechten Formen lassen sich umsortieren, wenn `onReorder` gesetzt ist. Ein Zug
 * beginnt erst nach kurzer Verzögerung oder etwas Weg — sonst würde jeder Tipp als
 * Verschieben gelten und die App ließe sich nicht mehr öffnen.
 */
export type AppSidebarVariant = "rail" | "icons" | "bottom";

const BOTTOM_MAX = 6;

export function AppSidebar({
  apps,
  variant,
  onNavigate,
  onReorder,
}: {
  apps: AppDescriptorDTO[];
  variant: AppSidebarVariant;
  onNavigate?: () => void;
  /** Ohne diese Angabe lässt sich nichts verschieben. */
  onReorder?: (order: string[]) => void;
}): JSX.Element {
  const [dragging, setDragging] = useState<AppDescriptorDTO | null>(null);

  // Beim Loslassen muss die Liste **sofort** umsortiert sein, sonst fällt das Symbol auf
  // seinen alten Platz und hüpft erst danach an den neuen — die gespeicherte Reihenfolge
  // kommt ja erst mit der Antwort des Servers zurück. Diese Übersteuerung hält den
  // Zwischenstand, bis die Daten nachgezogen sind.
  const [pendingOrder, setPendingOrder] = useState<string[] | null>(null);
  const propIds = apps.map((app) => app.id).join(",");

  useEffect(() => {
    setPendingOrder((current) => (current && current.join(",") === propIds ? null : current));
  }, [propIds]);

  const effective = pendingOrder ? orderApps(apps, pendingOrder) : apps;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 220, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const shown = variant === "bottom" ? effective.slice(0, BOTTOM_MAX) : effective;
  const sortable = onReorder !== undefined && variant !== "bottom";

  const onDragStart = (event: DragStartEvent): void => {
    setDragging(effective.find((app) => app.id === event.active.id) ?? null);
  };

  const onDragEnd = (event: DragEndEvent): void => {
    const over = event.over;
    if (over && over.id !== event.active.id) {
      const next = moveApp(shown, String(event.active.id), String(over.id));
      // Erst umsortieren, dann speichern: die Fallanimation landet so auf dem Platz,
      // den die Vorschau gezeigt hat.
      setPendingOrder(next);
      onReorder?.(next);
    }
    setDragging(null);
  };

  const list = (
    <nav
      aria-label={variant === "bottom" ? "Bereiche (untere Leiste)" : "Bereiche"}
      className={CONTAINER[variant]}
    >
      {shown.map((app) => (
        <AppSidebarLink
          key={app.id}
          app={app}
          variant={variant}
          sortable={sortable}
          onNavigate={onNavigate}
        />
      ))}
    </nav>
  );

  if (!sortable) {
    return list;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={() => setDragging(null)}
    >
      <SortableContext items={shown.map((app) => app.id)} strategy={verticalListSortingStrategy}>
        {list}
      </SortableContext>

      {/* Das mitgezogene Symbol schwebt über der Leiste, damit der Zug sichtbar bleibt. */}
      <DragOverlay dropAnimation={{ duration: 180, easing: "cubic-bezier(0.2, 0, 0, 1)" }}>
        {dragging ? (
          <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-brand/10 text-brand shadow-lg">
            <AppIcon name={dragging.icon} className="h-5 w-5" />
          </span>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

const CONTAINER: Record<AppSidebarVariant, string> = {
  rail: "hidden w-14 shrink-0 flex-col items-center gap-1 border-r border-line bg-surface-raised px-1 py-2 pb-safe-bottom pl-safe-left lg:flex",
  icons: "flex flex-col items-center gap-1",
  // Unten sitzt auf dem Telefon die Gestenleiste des Systems. Ohne diesen Abstand
  // lägen die Symbole darunter und ließen sich nur schwer treffen.
  bottom:
    "flex shrink-0 flex-row justify-around gap-1 border-t border-line bg-surface-raised px-1 py-1 pb-[calc(theme(spacing.1)+env(safe-area-inset-bottom))] lg:hidden",
};

const ITEM: Record<AppSidebarVariant, string> = {
  rail: "h-11 w-11 justify-center",
  icons: "h-11 w-11 justify-center",
  bottom: "min-w-0 flex-1 flex-col gap-1 px-1 py-1.5",
};

function AppSidebarLink({
  app,
  variant,
  sortable,
  onNavigate,
}: {
  app: AppDescriptorDTO;
  variant: AppSidebarVariant;
  sortable: boolean;
  onNavigate?: () => void;
}): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: app.id,
    disabled: !sortable,
  });

  // dnd-kit setzt `role="button"`. Das hier bleibt aber ein Link, der navigiert —
  // die Rolle darf nicht überschrieben werden, sonst kündigen Vorleseprogramme eine
  // Schaltfläche an, die in Wahrheit die Seite wechselt.
  const { role: _ignoredRole, ...dragAttributes } = attributes;

  const iconsOnly = variant === "rail" || variant === "icons";

  return (
    <NavLink
      ref={setNodeRef}
      style={
        sortable
          ? {
              transform: CSS.Translate.toString(transform),
              transition,
              // Der Platzhalter bleibt stehen; das Symbol selbst schwebt im Overlay.
              opacity: isDragging ? 0 : 1,
            }
          : undefined
      }
      to={app.route}
      end={app.route === "/"}
      title={app.name}
      onClick={onNavigate}
      {...(sortable ? dragAttributes : {})}
      {...(sortable ? listeners : {})}
      className={({ isActive }) =>
        [
          "flex items-center rounded-lg touch-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand",
          ITEM[variant],
          isActive ? "bg-brand/10 text-brand" : "text-ink-muted hover:bg-surface-sunken hover:text-ink",
        ].join(" ")
      }
    >
      <AppIcon name={app.icon} className="h-5 w-5 shrink-0" />
      {iconsOnly ? (
        // Nur Symbole: der Name steht im Tooltip und bleibt für Vorleseprogramme da.
        <span className="sr-only">{app.name}</span>
      ) : (
        <span className="line-clamp-2 w-full break-words text-center text-[9px] leading-tight sm:text-[10px]">
          {app.name}
        </span>
      )}
    </NavLink>
  );
}
